import { CSSProperties, forwardRef, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import COLORS from 'constants/colors';
import FONTS from 'constants/fonts';
import { InputHTMLAttributes } from 'react';
import { snakeCase } from 'lodash';
import ValidIcon from '../Icons/InlineTextValidIcon';
import InvalidIcon from '../Icons/InlineTextInvalidIcon';
import NeutralIcon from '../Icons/InlineTextNeutralIcon';

const StyledLabel = styled.label<{ leftBuffer: number }>`
  position: absolute;
  left: ${(props) => props.leftBuffer || 18}px; // 16px padding + 2px border
  line-height: 130%;
  top: 18px;
  pointer-events: none;
  transition: 0.2s;
  color: ${COLORS.GRAY.GRAY_600};
  font-size: ${FONTS.H5};
  font-family: Inter;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  width: calc(100% - 32px);
`;

interface StyledInputProps
  extends Exclude<InputHTMLAttributes<HTMLInputElement>, 'placeholder'> {
  invalid: boolean;
  type?: 'text' | 'password' | 'number';
  startAdornment?: JSX.Element;
  leftBuffer: number;
}

const StyledInput = styled.input<StyledInputProps>`
  width: 100%;
  border: 2px solid ${COLORS.GRAY.GRAY_200};
  ${(props) => props.invalid && `border-color: ${COLORS.RED.RED_500};`}
  background:  ${COLORS.GRAY.GRAY_200};
  border-radius: 4px;
  transition: border-color 0.2s;
  padding: 24px ${(props) => props.leftBuffer || 16}px 8px;
  font-size: ${FONTS.H5};
  font-family: Inter;
  color: ${COLORS.GRAY.GRAY_900};

  &:focus {
    outline: none;
  }
`;

const CharacterLimit = styled.p`
  position: absolute;
  transition: 0.2s;
  right: 12px;
  bottom: 8px;
  color: ${COLORS.GRAY.GRAY_400};
  font-size: 14px;
  z-index: 1;
`;

const LabeledInputDiv = styled.div`
  padding-bottom: 8px;
  position: relative;
  ${StyledInput}:focus + ${StyledLabel}, ${StyledInput}:not(:placeholder-shown) + ${StyledLabel} {
    top: 8px;
    font-size: ${FONTS.P};
  }
  ${StyledInput}:focus ~ ${CharacterLimit}, ${StyledInput}:not(:placeholder-shown) ~ ${CharacterLimit} {
    bottom: 16px;
    color: ${COLORS.GRAY.GRAY_600};
  }
`;

const InputDiv = styled.div<{ focused?: boolean }>`
  background: ${COLORS.WHITE};
  border: ${(props) =>
    props.focused
      ? `2px solid ${COLORS.FINCH.PURPLE}`
      : `1px solid ${COLORS.GRAY.GRAY_400}`};
  border-radius: 8px;
  position: relative;
`;

const HelperText = styled.div`
  color: ${COLORS.GRAY.GRAY_600};
  font-size: 12px;
  margin-top: 8px;
  padding-left: 16px;
`;

const lengthHelperTextColors = {
  valid: COLORS.GREEN,
  invalid: COLORS.RED.RED_600,
  neutral: COLORS.GRAY.GRAY_600,
};

const LengthHelperText = styled.div<{ state: 'valid' | 'invalid' | 'neutral' }>`
  color: ${(props) => lengthHelperTextColors[props.state]};
  font-size: 14px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  top: 100%;
`;

export interface InputProps extends Omit<StyledInputProps, 'leftBuffer'> {
  label: string;
  helperText?: string;
  characterLimit?: number;
}

const AdornmentContainer = ({
  adornment,
  setWidth,
}: {
  adornment?: JSX.Element;
  setWidth: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const refContainer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (refContainer.current) {
      setWidth(refContainer.current.offsetWidth);
    }
  }, [setWidth]);
  return (
    <div
      ref={refContainer}
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translate(0, -50%)',
      }}
    >
      {adornment}
    </div>
  );
};

const Input = forwardRef<HTMLInputElement, InputProps & { wrapperPaddingBottom?: CSSProperties['paddingBottom'] }>((props, ref) => {
  const { label, startAdornment, helperText, ...inputProps } = props;
  const [startAdornmentWidth, setStartAdornmentWidth] = useState(0);
  const [focused, setFocused] = useState<boolean>(false);
  const [lengthValidation, setLengthValidation] = useState<
    'valid' | 'invalid' | 'neutral' | 'hidden'
  >('hidden');
  const inputId = inputProps.id || `${snakeCase(label)}-input`;

  const getLengthValidationIcon = () => {
    switch (lengthValidation) {
      case 'valid':
        return <ValidIcon />;
      case 'invalid':
        return <InvalidIcon />;
      default:
        return <NeutralIcon />;
    }
  };

  const getLengthValidationText = () => {
    if (props.minLength === undefined) {
      return '';
    }

    if (props.minLength === props.maxLength) {
      return `has ${props.minLength} characters`;
    }

    if (props.maxLength === undefined) {
      return `is at least ${props.minLength} characters`;
    }

    return `is ${props.minLength}-${props.maxLength} characters`;
  };

  const style = {
    background: COLORS.WHITE,
    border: '0px',
    borderRadius: '8px',
    fontWeight: 400,
    color: '#161616',
    ...(props.style ?? {}),
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (
      props.characterLimit &&
      event.target.value.length > props.characterLimit
    ) {
      return;
    }

    if (props.minLength !== undefined) {
      setLengthValidation(
        event.target.value.length >= props.minLength ? 'valid' : 'neutral',
      );
    }

    inputProps.onChange?.(event);
  };

  const onInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);

    if (lengthValidation !== 'valid') {
      setLengthValidation('neutral');
    }

    inputProps.onFocus?.(event);
  };

  const onInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);

    if (
      props.minLength !== undefined &&
      event.target.value.length < props.minLength
    ) {
      setLengthValidation('invalid');
    }

    inputProps.onBlur?.(event);
  };

  const inputValue = inputProps.value?.toString();

  return (
    <LabeledInputDiv style={{ paddingBottom: props.wrapperPaddingBottom }}>
      <AdornmentContainer
        adornment={startAdornment}
        setWidth={setStartAdornmentWidth}
      />
      <InputDiv focused={focused}>
        <StyledInput
          ref={ref}
          id={inputId}
          {...inputProps}
          onChange={onInputChange}
          style={style}
          leftBuffer={startAdornmentWidth}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          placeholder=" "
        />
        <StyledLabel htmlFor={inputId} leftBuffer={startAdornmentWidth}>
          {props.characterLimit ? `${props.characterLimit}-digit ` : ''}
          {label}
        </StyledLabel>
        {props.characterLimit && (
          <CharacterLimit
            style={{
              color:
                inputValue?.length === props.characterLimit
                  ? COLORS.GREEN
                  : undefined,
            }}
          >
            {inputValue?.length}/{props.characterLimit}
          </CharacterLimit>
        )}
      </InputDiv>
      {helperText && <HelperText>{helperText}</HelperText>}
      {lengthValidation !== 'hidden' && props.minLength !== undefined && (
        <LengthHelperText state={lengthValidation}>
          {getLengthValidationIcon()} {label} {getLengthValidationText()}
        </LengthHelperText>
      )}
    </LabeledInputDiv>
  );
});

Input.displayName = 'Input';

Input.defaultProps = {
  type: 'text',
  autoFocus: false,
  disabled: false,
  autoComplete: 'off',
  autoCorrect: 'off',
  spellCheck: 'false',
  required: true,
};

export default Input;
